import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import icechestImage from "../assets/icechestwater.jpg";
import solonetVideo from "../assets/solonet.mp4";
import timberImage from "../assets/timber.jpg";

const projects = [
  {
    title: "Ice Chest Water",
    image: icechestImage,
    description:
      "A premium bottled water brand by Kwame O. Sarfo, delivering freshness, purity, and sustainability across Ghana.",
    website: "https://icechestwater.com",
  },
  {
    title: "Solonet Properties",
    media: solonetVideo,
    description:
      "A real estate and property development firm founded by Kwame O. Sarfo, focused on creating smart, eco-friendly residential and commercial spaces in Ghana.",
    website: "https://solonetproperties.com",
  },
  {
    title: "Global Timber Exports",
    image: timberImage,
    description:
      "Sustainably sourced premium timber export company owned by Kwame O. Sarfo, serving markets across Africa and beyond.",
    website: "https://example.com",
  },
];

const ProjectsShowcase = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Kwame O. Sarfo",
    jobTitle: "Entrepreneur",
    description:
      "Kwame O. Sarfo is an entrepreneur and investor in Ghana, leading ventures such as Solonet Properties, Ice Chest Water, and Global Timber Exports.",
    worksFor: [
      {
        "@type": "Organization",
        name: "Solonet Properties",
        url: "https://solonetproperties.com",
      },
      {
        "@type": "Organization",
        name: "Ice Chest Water",
        url: "https://icechestmineralwater.com",
      },
      {
        "@type": "Organization",
        name: "Global Timber Exports",
        url: "https://example.com",
      },
    ],
    sameAs: [
      "https://www.linkedin.com/in/kwameosarfo",
      "https://www.instagram.com/kwameosarfo",
    ],
  };

  return (
    <section
      id="projects"
      className="bg-black text-white py-24 px-6 md:px-20"
      aria-labelledby="projects-heading"
    >
      {/* SEO Metadata */}
      <Helmet>
        <title>Ventures & Projects | Kwame O. Sarfo</title>
        <meta
          name="description"
          content="Explore the ventures led by Kwame O. Sarfo  including Solonet Properties, Ice Chest Water, and Global Timber Exports — transforming industries through innovation and sustainability."
        />
        <meta
          name="keywords"
          content="Kwame O. Sarfo, Solonet Properties, Ice Chest Water, Global Timber, Ghana entrepreneur, real estate, timber exports, bottled water Ghana, business owner"
        />
        <meta property="og:title" content="Kwame O. Sarfo | Ventures & Projects" />
        <meta
          property="og:description"
          content="Discover the flagship ventures founded by Kwame O. Sarfo — Solonet Properties, Ice Chest Water, and Global Timber Exports."
        />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={icechestImage} />
        <meta property="og:url" content="https://kwameosarfo.com/ventures" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Section Header */}
      <div className="max-w-6xl mx-auto text-center mb-16">
        <motion.h2
          id="projects-heading"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-playfair text-[#d4af37]"
        >
          Ventures & Projects
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-gray-400 mt-4 max-w-3xl mx-auto leading-relaxed"
        >
          Discover key ventures led by{" "}
          <strong>Kwame O. Sarfo</strong> — brands that combine innovation,
          sustainability, and excellence to drive lasting impact in real estate,
          manufacturing, and natural resource industries.
        </motion.p>
      </div>

      {/* Project Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
        {projects.map((project, index) => (
          <motion.article
            key={index}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: index * 0.2 }}
            viewport={{ once: true }}
            onClick={() => setSelectedProject(project)}
            className="group relative overflow-hidden rounded-2xl shadow-lg border border-[#2a2a2a] hover:border-[#d4af37]/40 transition-all duration-500 cursor-pointer"
            aria-label={`Learn more about ${project.title}`}
          >
            <figure className="overflow-hidden">
              {project.media ? (
                <video
                  src={project.media}
                  autoPlay
                  muted
                  loop
                  playsInline
                  title={project.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
              ) : (
                <img
                  src={project.image}
                  alt={`${project.title} — ${project.description}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
              )}
            </figure>

            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-80 group-hover:opacity-100 transition duration-500"></div>

            {/* Text */}
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="text-xl font-playfair text-[#d4af37]">
                {project.title}
              </h3>
              <p className="text-gray-300 text-sm mt-2">{project.description}</p>
            </div>
          </motion.article>
        ))}
      </div>

      {/* Popup Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-[#111] text-white rounded-2xl p-8 max-w-lg w-full relative"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-[#d4af37] text-2xl"
                aria-label="Close project details"
              >
                &times;
              </button>

              {selectedProject.media ? (
                <video
                  src={selectedProject.media}
                  autoPlay
                  muted
                  loop
                  className="w-full h-56 object-cover rounded-lg mb-6"
                  title={selectedProject.title}
                />
              ) : (
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-56 object-cover rounded-lg mb-6"
                />
              )}

              <h3
                id="modal-title"
                className="text-2xl font-playfair text-[#d4af37] mb-3"
              >
                {selectedProject.title}
              </h3>
              <p className="text-gray-300 mb-6">
                {selectedProject.description}
              </p>

              {selectedProject.website && (
                <a
                  href={selectedProject.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#d4af37] text-black px-6 py-2 rounded-full font-semibold hover:bg-[#e0c05a] transition"
                >
                  Visit Website
                </a>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectsShowcase;
